// Persistent on-wall dimension annotations: a length pill per wall, billboarded
// toward the camera. When a wall's dimension is read on a face (not the
// centerline) a thin dimension line with perpendicular end ticks is drawn along
// that face, so it's unambiguous which side/span the number measures. The pill
// is kept a constant on-screen size (updateScales); the dimension line tracks
// the real geometry. Lengths are formatted by an injected formatter.

import * as THREE from "three";
import type { WallLabelInfo } from "./walls";
import { type SpriteLabel, makeLabel, setLabel } from "./sprite-label";

const Y = 0.06; // pill height (just above the floor + measure labels)
const LINE_Y = 0.055; // dimension line, a hair below the pill
const BASE_H = 0.155; // pill on-screen height; width follows the text (l.aspect)
const TICK = 0.08; // end-tick half-length (world metres), matching the model dims

export class WallLabels {
  readonly group = new THREE.Group();
  private labels: SpriteLabel[] = [];
  private lineGeom = new THREE.BufferGeometry();
  private lineMat: THREE.LineBasicMaterial;
  private lines: THREE.LineSegments;
  // While a wall's length is being edited in-place, hide its pill so the DOM
  // edit box stands in for it (same spot) instead of doubling up behind it.
  private hiddenWallId: number | null = null;
  // Per-wall XZ offset that pushes the pill OFF its dimension line each frame
  // (computed in updateScales, zoom-dependent). The in-place editor reads it so
  // its box lands where the offset pill was.
  private offsets = new Map<number, { dx: number; dz: number }>();
  // In 3D, the lines + pills should be hidden behind walls (depthTest on); the
  // 2D plan keeps them as an always-on-top overlay.
  private occluded = false;

  constructor(private fmt: (metres: number) => string) {
    const mat = new THREE.LineBasicMaterial({
      color: 0xffe87a,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    this.lineMat = mat;
    this.lines = new THREE.LineSegments(this.lineGeom, mat);
    this.lines.renderOrder = 11; // under the pills (12), over the walls
    // The geometry is rebuilt every frame in sync() and its bounding sphere
    // isn't recomputed, so leaving frustum culling on would cull the whole set
    // against a stale (often origin/zero) volume — making the lines vanish when
    // zoomed in. It's a handful of segments, so just never cull them.
    this.lines.frustumCulled = false;
    this.group.add(this.lines);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  // Hide one wall's pill (null = none) — used while editing its value in place.
  setHiddenWall(id: number | null): void {
    this.hiddenWallId = id;
  }

  // In 3D, depth-test the lines + pills so walls occlude them; in 2D keep them
  // always-on-top. Idempotent; labels are pooled so sync() applies it to new ones.
  setOccluded(on: boolean): void {
    if (on === this.occluded) return;
    this.occluded = on;
    this.lineMat.depthTest = on;
    for (const l of this.labels) l.sprite.material.depthTest = on;
  }

  // The current-frame offset that pushes this wall's pill off its dimension line
  // (zero for a centerline dim or an unknown wall) — for the in-place editor.
  pillOffset(id: number): { dx: number; dz: number } {
    return this.offsets.get(id) ?? { dx: 0, dz: 0 };
  }

  // Place a pill per wall and rebuild the dimension lines. A pooled sprite is
  // reused per wall; extras are hidden. The wall id is stashed on each sprite so
  // a raycast hit (dragging the pill to another side) maps back to its wall.
  // `isShown` decides which walls display their dim (e.g. global toggle on, or
  // the wall is selected); omitted = every wall shows. A non-displayed wall
  // contributes neither a pill nor a dimension line.
  sync(walls: WallLabelInfo[], isShown?: (id: number) => boolean): void {
    while (this.labels.length < walls.length) {
      const l = makeLabel();
      l.sprite.material.depthTest = !this.occluded ? false : true; // match the current view
      this.labels.push(l);
      this.group.add(l.sprite);
    }
    const pos: number[] = [];
    for (let i = 0; i < this.labels.length; i++) {
      const on = i < walls.length;
      const sp = this.labels[i].sprite;
      const display = on && (isShown ? isShown(walls[i].id) : true);
      // The pill hides while its value is edited in place (the DOM box stands in),
      // but the wall still "displays" — its dimension line stays drawn.
      sp.visible = display && walls[i].id !== this.hiddenWallId;
      sp.userData.wallId = display ? walls[i].id : null;
      if (!display) continue;
      const w = walls[i];
      setLabel(this.labels[i], this.fmt(w.length));
      // Base (un-offset) pill spot = the dimension-line midpoint; updateScales
      // pushes it OFF the line. Stash the outward normal so it knows which way.
      sp.userData.baseX = w.x;
      sp.userData.baseZ = w.z;
      sp.position.set(w.x, Y, w.z);
      // Centerline (side 0) is the neutral default — just the pill, no line. A
      // face dimension gets its line + pill on that face, capped with the same
      // perpendicular end ticks (the I-beam "H" look) as the model + opening
      // dimensions so every measurement line reads the same way.
      if (w.side !== 0) {
        pos.push(w.sx, LINE_Y, w.sz, w.ex, LINE_Y, w.ez); // dimension line
        const dx = w.ex - w.sx;
        const dz = w.ez - w.sz;
        const L = Math.hypot(dx, dz) || 1;
        const tx = (-dz / L) * TICK; // unit perpendicular × half-tick
        const tz = (dx / L) * TICK;
        pos.push(w.sx - tx, LINE_Y, w.sz - tz, w.sx + tx, LINE_Y, w.sz + tz); // end tick A
        pos.push(w.ex - tx, LINE_Y, w.ez - tz, w.ex + tx, LINE_Y, w.ez + tz); // end tick B
        // Unit outward normal — away from the wall, BEYOND the dimension line
        // (so the pill floats outside like the door/window dimension pills).
        const sgn = w.side > 0 ? -1 : 1;
        sp.userData.onx = (-dz / L) * sgn;
        sp.userData.onz = (dx / L) * sgn;
      } else {
        sp.userData.onx = 0;
        sp.userData.onz = 0;
      }
    }
    this.lineGeom.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  }

  // Keep the pills a constant on-screen HEIGHT (the caller supplies a per-
  // position scale factor ~1 at the base zoom); the width follows the pill's
  // text via its measured `aspect` so it never distorts. Also pushes each pill
  // OFF its dimension line (like the opening pills) so the line isn't bisected —
  // by the pill's half-extent along the outward normal (width when offset along
  // the sprite's X, height along its Z) + a small gap; zoom-dependent, hence here.
  updateScales(scaleAt: (x: number, z: number) => number): void {
    this.offsets.clear();
    for (const l of this.labels) {
      const sp = l.sprite;
      const wid = sp.userData.wallId;
      // Skip pooled/extra sprites — but STILL compute the offset for a pill
      // hidden because it's being edited in place. Its sprite is invisible, yet
      // the DOM edit box reads this offset each frame to sit where the (offset)
      // pill was; without it the box would collapse onto the dimension line.
      const editing = typeof wid === "number" && wid === this.hiddenWallId;
      if (!sp.visible && !editing) continue;
      const bx = (sp.userData.baseX as number) ?? sp.position.x;
      const bz = (sp.userData.baseZ as number) ?? sp.position.z;
      const s = scaleAt(bx, bz);
      const onx = (sp.userData.onx as number) ?? 0;
      const onz = (sp.userData.onz as number) ?? 0;
      let dx = 0;
      let dz = 0;
      if (onx !== 0 || onz !== 0) {
        const hw = (BASE_H * s * l.aspect) / 2; // sprite half-width (∥ world X)
        const hh = (BASE_H * s) / 2; // sprite half-height (∥ world Z, in plan)
        const d = Math.abs(onx) * hw + Math.abs(onz) * hh + BASE_H * s * 0.22;
        dx = onx * d;
        dz = onz * d;
      }
      sp.position.set(bx + dx, Y, bz + dz);
      if (typeof wid === "number") this.offsets.set(wid, { dx, dz });
      sp.scale.set(BASE_H * s * l.aspect, BASE_H * s, 1);
    }
  }
}
